import { AppConfig } from '@/appConfig';
import { createRouteFlags } from '@ringba/config/flags';
import { isRouteLevel } from '@ringba/config/route-levels';
import { createSystemFlagsClient, DelegatingFlagsClient } from '@ringba/system-flags';
import { withOverrides } from '@ringba/system-flags/overrides';
import Vue from 'vue';

/** Bounds each settle cycle while a provider exists. */
const SETTLE_TIMEOUT_MS = 5000;

/**
 * Datadog system flags (`@ringba/system-flags`) for AngularJS code and Vue 2 components.
 * The Datadog config comes from `AppConfig`, absent in environments without Datadog, where reads serve compiled defaults.
 *
 * Reads follow settled cycles only:
 * - A notification while the client is settled schedules a digest and bumps a counter every read touches, so templates and components both follow it.
 * - Between a superseded cycle and the next settle, a value read before keeps its last settled value.
 */
export class SystemFlagsService extends DelegatingFlagsClient {
  /** @ngInject **/
  constructor($rootScope, appStorages, v3Routes) {
    super(createClient(appStorages));
    this._state = Vue.observable({ version: 0 });
    this._cache = new Map();
    this._cacheVersion = 0;

    /** The current value of each build config flag, by name. */
    this.values = createValuesMap(this, __BUILD_CONFIG__.flags);

    /** Runtime view of the compiled `routes`, where each level can be overridden by its own system flag. */
    this.config = { routes: createValuesMap(this, createRouteFlags(v3Routes), isRouteLevel) };

    this._client.onChanged.on(() => {
      // A superseded cycle serves compiled defaults until the next one settles.
      if (!this._client.isSettled) {
        return;
      }
      this._state.version += 1;
      // A client notification arrives outside a digest.
      $rootScope.$applyAsync();
    });
  }

  /**
   * The flag's current value, synchronously; never throws.
   * Evaluates each flag once per settled snapshot, since the provider tracks every evaluation.
   */
  get(flag) {
    const version = this._state.version; // reactive dep
    if (version !== this._cacheVersion) {
      this._cache.clear();
      this._cacheVersion = version;
    }
    if (!this._cache.has(flag.key)) {
      this._cache.set(flag.key, super.get(flag));
    }
    return this._cache.get(flag.key);
  }
}

function createClient(storages) {
  // Both portals keep the targeting key under this key, so a percentage rollout buckets one browser the same way in each.
  const targetingKey = storages.local.getObjectBoxed('systemFlags.targetingKey');

  const client = createSystemFlagsClient({
    datadog: AppConfig.datadog,
    targetingKeyStore: {
      get: () => targetingKey.getValue(),
      set: (value) => targetingKey.setValue(value),
    },
    timeoutMs: SETTLE_TIMEOUT_MS,
  });

  return __BUILD_CONFIG__.devTools.hasFlagOverrides ? withOverrides(client, storages.session.toStringSync()) : client;
}

/** Live values over a flag-definition map; a served value that `accepts` rejects reads as the definition's default. */
function createValuesMap(client, definitions, accepts) {
  const values = {};
  for (const [name, flag] of Object.entries(definitions)) {
    Object.defineProperty(values, name, {
      enumerable: true,
      get: () => {
        const served = client.get(flag);
        return accepts && !accepts(served) ? flag.defaultValue : served;
      },
    });
  }
  return values;
}
